import React, { useMemo, useState } from 'react';
import { DailyMetrics, PHQ4Entry } from '../types';
import { computeRelapseRisk, RelapseSignal, HomeLeavingDay } from '../lib/relapse';

// ─── Signal Icon ──────────────────────────────────────────────────────────────

function SignalIcon({ direction }: { direction: RelapseSignal['direction'] }) {
  if (direction === 'risk') {
    return <span className="text-amber-400 text-sm">◉</span>;
  }
  if (direction === 'protective') {
    return <span className="text-green-500 text-sm">◉</span>;
  }
  return <span className="text-gray-400 text-sm">◎</span>;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface RelapseWarningProps {
  metrics: DailyMetrics[];
  phq4Entries: PHQ4Entry[];
  homeLeaving?: HomeLeavingDay[];
}

export default function RelapseWarning({ metrics, phq4Entries, homeLeaving }: RelapseWarningProps) {
  const [expanded, setExpanded] = useState(false);

  const risk = useMemo(
    () => computeRelapseRisk(metrics, phq4Entries, homeLeaving),
    [metrics, phq4Entries, homeLeaving]
  );

  // Don't render if no data at all
  if (risk.signals.length === 0) return null;

  const bgColor = risk.level === 'high' ? 'bg-red-50 border-red-200'
    : risk.level === 'elevated' ? 'bg-orange-50 border-orange-200'
    : risk.level === 'moderate' ? 'bg-amber-50 border-amber-200'
    : 'bg-green-50 border-green-200';

  const ringColor = risk.color;

  return (
    <div className={`card border ${bgColor}`}>
      {/* Header row */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          {/* Risk ring */}
          <div className="relative flex-shrink-0" style={{ width: 48, height: 48 }}>
            <svg width={48} height={48} style={{ transform: 'rotate(-90deg)' }}>
              <circle cx={24} cy={24} r={20} fill="none" stroke="#E5E7EB" strokeWidth={5} />
              <circle
                cx={24} cy={24} r={20}
                fill="none"
                stroke={ringColor}
                strokeWidth={5}
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 20}
                strokeDashoffset={2 * Math.PI * 20 * (1 - risk.score / 100)}
                style={{ transition: 'stroke-dashoffset 0.6s ease' }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-bold" style={{ color: ringColor }}>
                {risk.score}
              </span>
            </div>
          </div>

          <div>
            <h2 className="text-sm font-bold text-arete-navy">Wellness Patterns</h2>
            <p className="text-xs" style={{ color: ringColor }}>
              {risk.level === 'low' ? 'Tracking well' : risk.level === 'moderate' ? 'A few mild shifts' : risk.level === 'elevated' ? 'Worth staying aware' : 'Worth some attention'}
              {risk.dataQuality < 60 && (
                <span className="text-arete-muted ml-1">· {risk.dataQuality}% data coverage</span>
              )}
            </p>
          </div>
        </div>

        <span className="text-arete-muted text-xs flex-shrink-0">
          {expanded ? '▼' : '▶'}
        </span>
      </button>

      {/* Summary (always shown) */}
      <p className="text-xs text-arete-muted mt-2">{risk.summary}</p>

      {/* Expanded detail */}
      {expanded && (
        <div className="mt-4 space-y-3">
          {/* Signal breakdown */}
          {risk.signals.map(s => (
            <div key={s.key} className="flex items-start gap-2">
              <SignalIcon direction={s.direction} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-arete-navy">{s.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-arete-muted">{s.value}</span>
                    {/* Mini bar */}
                    <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, s.score)}%`,
                          backgroundColor: s.score > 60 ? '#EF4444' : s.score > 30 ? '#F59E0B' : '#22C55E',
                        }}
                      />
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-arete-muted mt-0.5">{s.description}</p>
              </div>
            </div>
          ))}

          {/* Research basis */}
          <div className="pt-2 border-t border-gray-100">
            <p className="text-[10px] text-arete-muted leading-relaxed">
              Patterns based on published research: JAMA Psychiatry 2026 (actigraphy), npj Digital Medicine 2024 (circadian), eBioMedicine/Lancet 2024 (circadian→mood). For personal awareness only — not a clinical assessment.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
